// src/components/MonitoringProject/ScheduleReport/ScheduleReport.tsx
import { useQuery } from '@tanstack/react-query';
import { FiClock, FiCheckCircle, FiAlertTriangle, FiRefreshCw, FiExternalLink } from 'react-icons/fi';
import { getScheduleReport } from '../../../api/monitoring';
import styles from './ScheduleReport.module.css';

interface ScheduleReportProps {
  projectId: string;
}

export function ScheduleReport({ projectId }: ScheduleReportProps) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['schedule-report', projectId],
    queryFn: () => getScheduleReport(projectId),
    refetchInterval: 60000, // Refresh every minute
    enabled: !!projectId,
  });

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <FiRefreshCw className={styles.spinIcon} size={20} />
          <span>Loading schedule...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <FiAlertTriangle size={20} />
          <span>Failed to load schedule report</span>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const nextCrawl = new Date(data.project_next_crawl_at);
  const now = new Date(data.current_time);
  const hoursUntil = (nextCrawl.getTime() - now.getTime()) / (1000 * 60 * 60);
  const lastCrawl = data.project_last_crawl_at ? new Date(data.project_last_crawl_at) : null;


  // Format info messages to make timestamps human-readable
  const formatInfoMessage = (msg: string): string => {
    // Match ISO timestamps like "2026-01-04T18:01:29.708546Z" or "2026-01-04T18:01:29.708546"
    const timestampRegex = /(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z)?)/g;

    return msg.replace(timestampRegex, (match) => {
      try {
        const date = new Date(match);
        return date.toLocaleString();
      } catch {
        return match;
      }
    });
  };

  const infos = data.what_will_happen
    .filter(msg =>
      msg.includes('💡') || msg.includes('📊') || msg.includes('📄') || msg.includes('⏰')
    )
    .map(formatInfoMessage);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FiClock className={styles.headerIcon} />
        <h3>Next Crawl Schedule</h3>
      </div>

      <div className={styles.content}>
        {data.project_will_crawl ? (
          <div className={styles.statusReady}>
            <FiCheckCircle className={styles.iconSuccess} size={24} />
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>Ready to crawl</p>
              <p className={styles.statusSubtitle}>
                {data.summary.pages_due_now > 0
                  ? `${data.summary.pages_due_now} ${data.summary.pages_due_now === 1 ? 'page is' : 'pages are'} due now`
                  : 'Crawl will start but no pages are due yet'
                }
              </p>
              {lastCrawl && (
                <p className={styles.statusMuted}>
                  Last crawl: {lastCrawl.toLocaleString()}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className={styles.statusScheduled}>
            <FiClock className={styles.iconInfo} size={24} />
            <div className={styles.statusText}>
              <p className={styles.statusTitle}>
                Next crawl in {Math.abs(hoursUntil).toFixed(1)} hours
              </p>
              <p className={styles.statusSubtitle}>
                {nextCrawl.toLocaleString()}
              </p>
              <p className={styles.statusMuted}>
                {data.summary.pages_scheduled} {data.summary.pages_scheduled === 1 ? 'page' : 'pages'} scheduled
              </p>
              {lastCrawl && (
                <p className={styles.statusMuted}>
                  Last crawl: {lastCrawl.toLocaleString()}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Tier breakdown if tier-based scheduling is enabled */}
        {data.tier_based_scheduling_enabled && data.tier_frequencies && (
          <div className={styles.tierSummary}>
            <p className={styles.tierLabel}>Tier Frequencies:</p>
            <div className={styles.tierBadges}>
              <span className={`${styles.tierBadge} ${styles.tierA}`}>
                Tier A: {data.tier_frequencies.tier_a_frequency}
              </span>
              <span className={`${styles.tierBadge} ${styles.tierB}`}>
                Tier B: {data.tier_frequencies.tier_b_frequency}
              </span>
              <span className={`${styles.tierBadge} ${styles.tierC}`}>
                Tier C: {data.tier_frequencies.tier_c_frequency}
              </span>
            </div>
          </div>
        )}

        {/* Show info messages */}
        {infos.length > 0 && (
          <div className={styles.infoBox}>
            <ul className={styles.infoList}>
              {infos.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </div>
        )}


        {/* Summary stats */}
        <div className={styles.summary}>
          <div className={styles.stat}>
            <span className={styles.statValue}>{data.summary.total_pages}</span>
            <span className={styles.statLabel}>Total Pages</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{data.summary.pages_due_now}</span>
            <span className={styles.statLabel}>Due Now</span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statValue}>{data.summary.pages_scheduled}</span>
            <span className={styles.statLabel}>Scheduled</span>
          </div>
          {data.summary.pages_never_crawled > 0 && (
            <div className={styles.stat}>
              <span className={styles.statValue}>{data.summary.pages_never_crawled}</span>
              <span className={styles.statLabel}>Never Crawled</span>
            </div>
          )}
        </div>

        {/* Pages scheduled soon */}
        {data.pages_scheduled_soon && data.pages_scheduled_soon.length > 0 && (
          <div className={styles.pagesSection}>
            <h4 className={styles.pagesSectionTitle}>Next Pages to Crawl</h4>
            <div className={styles.pagesList}>
              {data.pages_scheduled_soon.slice(0, 5).map((page) => (
                <div key={page.page_id} className={styles.pageItem}>
                  <div className={styles.pageInfo}>
                    <span className={`${styles.pageTierBadge} ${styles[`tier${page.tier_effective}`]}`}>
                      {page.tier_effective}
                      {page.tier_manual && <span className={styles.manualIndicator}>✏️</span>}
                    </span>
                    <a
                      href={page.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.pageUrl}
                      title={page.url}
                    >
                      {page.url.length > 60 ? `${page.url.substring(0, 60)}...` : page.url}
                      <FiExternalLink size={12} className={styles.externalIcon} />
                    </a>
                  </div>
                  <div className={styles.pageSchedule}>
                    {page.is_overdue ? (
                      <span className={styles.overdue}>
                        Overdue by {page.time_until_due}
                      </span>
                    ) : (
                      <span className={styles.scheduled}>
                        Due in {page.time_until_due}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {data.pages_scheduled_soon.length > 5 && (
              <p className={styles.morePages}>
                + {data.pages_scheduled_soon.length - 5} more pages scheduled
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

